import { Component } from 'component';
import { debounce, isClickedOutside, onAnimationEnd, prefersReducedMotion, isMobileBreakpoint } from 'utilities';
import { sectionRenderer } from 'section-renderer';
import { morph } from 'morph';
import { ThemeEvents, ToggleDialogEvent } from 'theme-events';
import { RecentlyViewed } from 'recently-viewed-products';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} predictiveSearchResultsFooter - The predictive search results footer.
 * @property {HTMLElement} predictiveSearchHeader - The predictive search header.
 * @property {HTMLElement} searchButton - The search button element.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement[]} [predictiveSearchCarousels] - The predictive search carousels elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'searchInput',
    'predictiveSearchResults',
    'predictiveSearchResultsFooter',
    'predictiveSearchHeader',
    'searchButton',
    'resetButton',
  ];

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {EventListener | null} */
  #handleModalScroll = null;
  /** @type {EventListener | null} */
  #handleModalResize = null;
  /** @type {(() => void) | null} */
  #dialogResetSearch = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  connectedCallback() {
    super.connectedCallback();

    this.refs.searchInput.addEventListener('focus', () => {
      if (!this.hasBeenOpened) {
        this.refs.searchInput.value = '';
        this.#hideResetButton();
        this.hasBeenOpened = true;
      }
    });

    if (this.dataset.sectionStyle === 'modal') {
      const dialog = this.closest('dialog');
      if (dialog) {
        this.#dialogObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'open') {
              this.resetSearch();
            }
          });
        });

        this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    this.addEventListener('mousedown', (event) => {
      event.preventDefault();

      this.#isKeyboardNavigation = false;

      this.#allResultsItems?.forEach((item) => {
        item.classList.remove('keyboard-focus');
      });
    });

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (this.dataset.sectionStyle === 'modal') {
      document.addEventListener('keydown', this.#CMDKListener);
    }
    document.addEventListener(ThemeEvents.megaMenuHover, this.#closeSearch);

    this.#getRecentlyViewed();

    // TODO: Could we put a ref directly on the results child
    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.hasClickOutsideListener = false;
    this.hasBeenOpened = false;
    document.removeEventListener('click', this.#resetSearchOnClickOutside);
    if (this.dataset.sectionStyle === 'modal') {
      document.removeEventListener('keydown', this.#CMDKListener);
      if (this.#handleModalResize) {
        window.removeEventListener('resize', this.#handleModalResize);
      }

      const dialog = this.closest('dialog');
      if (dialog) {
        // Cleanup scroll handler
        if (this.#handleModalScroll) {
          dialog.removeEventListener('scroll', this.#handleModalScroll);
        }

        if (this.#dialogObserver) {
          this.#dialogObserver.disconnect();
        }
      }
    }
    document.removeEventListener(ThemeEvents.megaMenuHover, this.#closeSearch);
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #CMDKListener = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dispatchEvent(new ToggleDialogEvent(this));
    }
  };

  /**
   * Set the width of the search component.
   * @param {number} width - The width of the search component.
   */
  set #searchBaseWidthValue(width) {
    this.style.setProperty('--search-width', `${width}`);
  }

  /**
   * Get the width of the search component.
   * @returns {number} The width of the search component.
   */
  get #searchBaseWidthValue() {
    return parseInt(this.style.getPropertyValue('--search-width'));
  }

  expandSearch = () => {
    // Get the header_column element the search is in
    const columnContainer = this.closest('.header__column');
    if (!(columnContainer instanceof HTMLElement)) return;

    this.#searchBaseWidthValue = this.offsetWidth;

    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      if (this.dataset.sectionStyle === 'modal') {
        const parentDialog = this.closest('dialog');
        if (parentDialog) {
          parentDialog.classList.add(`color-${this.dataset.activeColorScheme}`);
        }
      } else {
        this.classList.add(`color-${this.dataset.activeColorScheme}`);
      }
    }
  };

  get #allResultsItems() {
    const { resultsItems, predictiveSearchCarousels } = this.refs;

    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        break;
    }
  };

  clearRecentlyViewedProducts() {
    RecentlyViewed.clearProducts();

    const recentlyViewedItems = this.querySelectorAll('[data-recently-viewed]');
    const container = this.refs.predictiveSearchResults;
    const initialHeight = container.offsetHeight;

    recentlyViewedItems.forEach((element) => {
      element.classList.add('removing');
    });

    setTimeout(() => {
      recentlyViewedItems.forEach((element) => {
        element.remove();
      });
    }, 300);
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    const url = new URL(Shopify.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const initialHeight = predictiveSearchResults.offsetHeight ?? 0;

    predictiveSearchResults.style.height = `auto`;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();

        this.#openResults(initialHeight);
        if (predictiveSearchResults.querySelector('.predictive-search-results__no-results')) {
          this.#hideFooter();
        } else {
          this.#showFooter();
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        this.#closeResults(initialHeight);
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Shopify.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  async #getRecentlyViewed() {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const initialHeight = predictiveSearchResults.offsetHeight ?? 0;
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!resultsMarkup) return;

      const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      // Set an attribute so we can recognize the recently viewed products
      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('data-recently-viewed', '');
        }
      }

      const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      // If there are existing recently viewed products, remove them
      const existingRecentlyViewedProducts = collectionElement.querySelectorAll('[data-recently-viewed]');
      if (existingRecentlyViewedProducts.length > 0) {
        for (const product of existingRecentlyViewedProducts) {
          product.remove();
        }
      }

      if (abortController.signal.aborted) return;
      // Prepend the recently viewed products to the collection
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    } catch (error) {
      throw error;
    }
  }

  #showFooter() {
    const { predictiveSearchResultsFooter, searchButton, searchInput } = this.refs;

    predictiveSearchResultsFooter.hidden = false;
    // Render and capture the string to avoid issues with special characters
    const decodedString = new DOMParser().parseFromString(
      Shopify.theme_strings.search_results_view_all ?? '',
      'text/html'
    ).body.textContent;

    searchButton.innerText = decodedString?.replace('[query]', searchInput.value) ?? '';
  }

  #hideFooter() {
    const { predictiveSearchResultsFooter, searchButton } = this.refs;

    predictiveSearchResultsFooter.hidden = true;
    searchButton.innerText = '';
  }

  /**
   * Debounce handler to reset the search state when clicking outside.
   * Clears the input, results, and selection state.
   * @param {MouseEvent} event - The mouse event.
   */
  #resetSearchOnClickOutside = debounce((event) => {
    if (isClickedOutside(event, this)) {
      this.#resetSearch();

      this.refs.searchInput.blur();
    }
  }, 100);

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.classList.add('predictive-search__reset-button--hidden');
    onAnimationEnd(resetButton, () => {
      resetButton.setAttribute('hidden', '');
    });
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    requestAnimationFrame(() => {
      resetButton.removeAttribute('hidden');
      resetButton.classList.remove('predictive-search__reset-button--hidden');
    });
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';
    const initialHeight = predictiveSearchResults.offsetHeight ?? 0;

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;
      // Set an attribute so we can recognize the recently viewed products
      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('data-recently-viewed', '');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#closeResults(initialHeight);
    this.#hideFooter();
    this.#hideResetButton();
  };

  /**
   * Shows the predictive search results
   * @param {number} initialHeight - The initial height of the predictive search results.
   */
  #openResults(initialHeight) {
    const { predictiveSearchResults } = this.refs;

    // Check if the event listener is already added by storing a flag
    if (!this.hasClickOutsideListener) {
      document.addEventListener('click', this.#resetSearchOnClickOutside);
      this.hasClickOutsideListener = true;
    }
  }

  /**
   * Hides the predictive search results
   * @param {number} initialHeight - The initial height of the predictive search results.
   */
  #closeResults(initialHeight) {
    const { predictiveSearchResults } = this.refs;
    document.removeEventListener('click', this.#resetSearchOnClickOutside);
  }

  /**
   * Closes the predictive search.
   */
  #closeSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
